/** Helpers for MAPI quote `data` body (e.g. session `mapiQuoteResponse`) and related request shaping. */

import { quoteFeeRequestDefaults } from "../constants/send.constant";

export function getMapiQuoteTransactions(
  quoteData: any | null | undefined
): any[] {
  const txs = quoteData?.transactions;
  return Array.isArray(txs) ? txs : [];
}

export function buildMapiCurrencyOptionsFromTransactions(
  transactions: any[]
): Array<{ currency: string; rate: number }> {
  if (!Array.isArray(transactions)) return [];
  const seen = new Map<string, number>();
  for (const tx of transactions) {
    const curr = tx?.receiveAmount?.amount?.currencyCode;
    const rate = tx?.receiveAmount?.fxRate;
    if (curr && !seen.has(curr)) {
      seen.set(curr, Number(rate) || 1);
    }
  }
  return Array.from(seen.entries()).map(([currency, rate]) => ({
    currency,
    rate,
  }));
}

export function formatQuoteDataErrors(data: any): string {
  const errs = data?.errors;
  if (!Array.isArray(errs) || errs.length === 0) return "";
  return errs
    .map((e: any) => e?.message || e?.Message || String(e?.code || ""))
    .filter(Boolean)
    .join("; ");
}

export function mapiPromotionInfoHasError(promotionInfo: unknown): boolean {
  if (!Array.isArray(promotionInfo)) return false;
  return promotionInfo.some(
    (p: any) =>
      !!(p?.PromotionErrorCode || p?.promotionErrorCode || p?.errorCode)
  );
}

/** Re-align selected MAPI transaction after a new quote (new `transactionId`s). */
export function findMatchingMapiTransaction(
  transactions: any[] | undefined,
  current: any | null
): any | null {
  if (!Array.isArray(transactions) || transactions.length === 0) return null;
  if (!current || typeof current !== "object") return transactions[0];
  const svc = String(current.serviceOptionCode || "").toUpperCase();
  const recvCurr = current?.receiveAmount?.amount?.currencyCode || "";
  if (svc) {
    const exact = transactions.find(
      (t: any) =>
        String(t?.serviceOptionCode || "").toUpperCase() === svc &&
        (!recvCurr ||
          (t?.receiveAmount?.amount?.currencyCode || "") === recvCurr)
    );
    if (exact) return exact;
    const svcOnly = transactions.find(
      (t: any) => String(t?.serviceOptionCode || "").toUpperCase() === svc
    );
    if (svcOnly) return svcOnly;
  }
  const tid = current.transactionId;
  if (tid) {
    const byTid = transactions.find((t: any) => t?.transactionId === tid);
    if (byTid) return byTid;
  }
  const recvAgent = current?.receiveAgentId;
  if (recvAgent != null && String(recvAgent) !== "") {
    const byAgent = transactions.find(
      (t: any) => String(t?.receiveAgentId ?? "") === String(recvAgent)
    );
    if (byAgent) return byAgent;
  }
  return transactions[0];
}

/** Fields read from session when building a standard MAPI quote request. */
export type MapiQuoteSessionSnapshot = {
  agentId?: string | null;
  selectedDestinationCountryCode?: string | null;
  pendingSendOption?: any;
  selectedReceiveCurrency?: string | null;
  selectedDestinationStateAbbr?: string | null;
};

export function buildMapiQuoteParamsFromSessionState(
  s: MapiQuoteSessionSnapshot,
  promotionCodes?: string[]
): Record<string, unknown> | null {
  const agent = s.agentId;
  const dest = s.selectedDestinationCountryCode;
  const pending = s.pendingSendOption;
  if (!agent || !dest || !pending) return null;
  const sendVal = Number(pending?.sendAmount?.amount?.value ?? 0);
  const sendCurr = pending?.sendAmount?.amount?.currencyCode || "USD";
  const recvCurr =
    s.selectedReceiveCurrency ||
    pending?.receiveAmount?.amount?.currencyCode ||
    undefined;
  const quoteParams: Record<string, unknown> = {
    agentPartnerId: agent,
    destinationCountryCode: dest,
    sendAmount: { value: sendVal, currencyCode: sendCurr },
  };
  if (recvCurr) quoteParams.receiveCurrencyCode = recvCurr;
  const subdiv = s.selectedDestinationStateAbbr;
  if (subdiv) quoteParams.destinationCountrySubdivisionCode = subdiv;
  if (promotionCodes && promotionCodes.length > 0) {
    quoteParams.promotionCodes = promotionCodes;
  }
  quoteParams.fundInStore = {
    fundInStore: quoteFeeRequestDefaults.fundInStore,
    fundInStoreAgentPartnerId: agent,
  };
  return quoteParams;
}

export function pickNonUsdTransaction(transactions: any[]): any | null {
  if (!Array.isArray(transactions) || transactions.length === 0) return null;
  const nonUsd = transactions.find(
    (tx: any) =>
      tx?.receiveAmount?.amount?.currencyCode &&
      tx.receiveAmount.amount.currencyCode !== "USD"
  );
  return nonUsd || transactions[0];
}

export function getFxFromQuoteData(data: any): {
  rate: number;
  receiveCurrency: string;
} {
  const transactions = getMapiQuoteTransactions(data);
  const tx = pickNonUsdTransaction(transactions);
  const rate = Number(tx?.receiveAmount?.fxRate ?? 1) || 1;
  const receiveCurrency = tx?.receiveAmount?.amount?.currencyCode || "";
  return { rate, receiveCurrency };
}

/** Map a MAPI quote transaction into a FeeInfo-shaped object for shared helpers and HowTheyReceiveModal. */
export function mapiTransactionToFeeInfoLike(
  tx: any,
  fallbackRate: number,
  fallbackRecv: string
): any {
  if (!tx || typeof tx !== "object") {
    return {
      __mapiTx: tx,
      SendAmounts: { SendCurrency: "USD", TotalSendFees: 0 },
      EstimatedExchangeRate: fallbackRate,
      EstimatedReceiveCurrency: fallbackRecv,
      DeliveryOption: "",
      DeliveryOptDisplayName: "",
      ReceiveAgentId: "",
      ReceiveAmounts: {},
    };
  }
  const sendCur =
    tx?.sendAmount?.total?.currencyCode ??
    tx?.sendAmount?.amount?.currencyCode ??
    "USD";
  const feeVal = Number(tx?.sendAmount?.fees?.value ?? 0);
  const recvCurr =
    tx?.receiveAmount?.amount?.currencyCode || fallbackRecv || "";
  const fx = Number(tx?.receiveAmount?.fxRate ?? fallbackRate ?? 0);
  const code = String(tx?.serviceOptionCode || "").toUpperCase();
  return {
    __mapiTx: tx,
    DeliveryOption: code,
    DeliveryOptDisplayName: tx?.serviceOptionName || "",
    ReceiveAgentName:
      tx?.receiveAgentName ??
      tx?.agentPartnerName ??
      tx?.serviceOptionName ??
      "",
    ReceiveAgentId: String(tx?.receiveAgentId ?? tx?.transactionId ?? ""),
    ReceiveAgentAbbreviation: tx?.receiveAgentAbbreviation ?? "",
    EstimatedExchangeRate: fx,
    ValidExchangeRate: fx,
    EstimatedReceiveCurrency: recvCurr,
    ValidReceiveCurrency: recvCurr,
    SendAmounts: {
      SendCurrency: sendCur,
      TotalSendFees: feeVal,
    },
    ReceiveAmounts: {},
  };
}
